import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, ArrowUp, Wallet, X, User, Phone } from 'lucide-react'
import { useCoreController } from '@/services/coreController'
import { AuthContext } from '@/contexts/authContext'
import { affiliateService } from '@/services/affiliateService'
import { toast } from 'sonner'

export function AffiliateWithdrawPage() {
  const navigate = useNavigate()
  const coreController = useCoreController()
  const { user } = useContext(AuthContext)

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableBalance, setAvailableBalance] = useState(0)
  const [amount, setAmount] = useState("")
  const [formData, setFormData] = useState({
    phoneNumber: user?.phone_number || '',
    displayName: user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const MIN_WITHDRAWAL = 100
  const themeColor = getAccountColor(coreController.compte.type)

  useEffect(() => {
    loadBalance()
  }, [user?.id])

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        phoneNumber: user.phone_number || prev.phoneNumber,
        displayName: user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : prev.displayName,
      }))
    }
  }, [user])

  const loadBalance = async () => {
    if (!user?.id) return

    setIsLoading(true)
    const result = await affiliateService.fetchAffiliateBalance(user.id)
    setIsLoading(false)

    if (result.success && result.balance != null) {
      setAvailableBalance(result.balance)
    } else {
      toast.error(result.error || 'Impossible de récupérer votre solde')
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleAmountChange = (value: string) => {
    const digits = value.replace(/\D/g, '')
    setAmount(digits)
    if (errors.amount) {
      setErrors((prev) => ({ ...prev, amount: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Numéro de téléphone requis'
    } else if (!/^[+]?[0-9]{8,}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = 'Numéro invalide'
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Nom requis'
    } else if (formData.displayName.trim().length < 3) {
      newErrors.displayName = 'Minimum 3 caractères'
    }

    const parsedAmount = parseInt(amount || '0', 10)
    if (!amount || parsedAmount < MIN_WITHDRAWAL) {
      newErrors.amount = `Minimum ${MIN_WITHDRAWAL} FCFA`
    } else if (parsedAmount > availableBalance) {
      newErrors.amount = 'Montant supérieur au solde disponible'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm() || !user?.id) return

    setIsSubmitting(true)
    const result = await affiliateService.requestWithdrawal(
      user.id,
      parseInt(amount, 10),
      formData.phoneNumber
    )
    setIsSubmitting(false)

    if (result.success) {
      toast.success('Demande de retrait envoyée')
      setTimeout(() => navigate('/dashboard'), 1500)
    } else {
      toast.error(result.error || 'Erreur lors du retrait')
    }
  }

  const canWithdraw = availableBalance >= MIN_WITHDRAWAL

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full bg-white shadow hover:shadow-md transition-shadow"
            style={{ color: themeColor }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold" style={{ color: themeColor }}>
            Retrait de commissions
          </h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-5">
        {/* Solde */}
        <Card
          className="border-0 shadow-md"
          style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}DD)` }}
        >
          <CardContent className="p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white/20 rounded-full">
                <Wallet className="w-6 h-6" />
              </div>
              <p className="text-white/90 font-medium">Solde commissions</p>
            </div>
            <div className="text-3xl font-bold">
              {isLoading ? '...' : `${availableBalance.toLocaleString('fr-FR')} FCFA`}
            </div>
          </CardContent>
        </Card>

        {/* Formulaire */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="amount" className="text-sm font-medium text-gray-700">
                  Montant à retirer
                </Label>
                <div className="relative mt-1">
                  <Wallet className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    id="amount"
                    type="text"
                    inputMode="numeric"
                    placeholder={`Maximum ${availableBalance.toLocaleString('fr-FR')} FCFA`}
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="pl-10"
                    disabled={isSubmitting || !canWithdraw}
                  />
                </div>
                {errors.amount && (
                  <p className="text-sm text-red-600 flex items-center mt-1">
                    <X className="w-3 h-3 mr-1" />
                    {errors.amount}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="displayName" className="text-sm font-medium text-gray-700">
                  Nom complet
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Votre nom complet"
                    value={formData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    className="pl-10"
                    disabled={isSubmitting || !canWithdraw}
                  />
                </div>
                {errors.displayName && (
                  <p className="text-sm text-red-600 flex items-center mt-1">
                    <X className="w-3 h-3 mr-1" />
                    {errors.displayName}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phoneNumber" className="text-sm font-medium text-gray-700">
                  Numéro Mobile Money
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+226 XX XX XX XX"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className="pl-10"
                    disabled={isSubmitting || !canWithdraw}
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600 flex items-center mt-1">
                    <X className="w-3 h-3 mr-1" />
                    {errors.phoneNumber}
                  </p>
                )}
              </div>

              {!canWithdraw && !isLoading && (
                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  Solde insuffisant. Minimum {MIN_WITHDRAWAL} FCFA.
                </p>
              )}

              <Button
                type="submit"
                className="w-full py-3 text-white font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                style={{ backgroundColor: themeColor }}
                disabled={isSubmitting || isLoading || !canWithdraw}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Envoi...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Retirer {(parseInt(amount || '0', 10)).toLocaleString('fr-FR')} FCFA
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function getAccountColor(accountType: string | undefined): string {
  switch (accountType?.toUpperCase()) {
    case '1XBET':
      return '#002366'
    case 'BETWINNER':
      return '#00C853'
    default:
      return '#FF9800'
  }
}

export default AffiliateWithdrawPage
